import { LargeHeadingStyled } from "../../../components/custom/texts/texts.styled";
import { Button, Card, Drawer, Dropdown, Flex, Form, Icon } from "@nubeteck/components";
import { Input, Layout, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useState, useEffect } from "react";
import { FormInstance } from "antd/lib/form";
import {
  useErrorSavingDataToast,
  useErrorUpdatingBankStateToast,
  useSuccessUpdateToast,
  useSuccessRegisterToast,
} from "src/constants";
import {
  useSetBankStateActiveMutation,
  useSetBankStateInactiveMutation,
  useCreateBankMutation,
  useGetBanksMutation,
  useUpdateBankMutation,
} from "src/services/bankServices";
import { getSettingsPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";

const { Content } = Layout;
const SCREEN_NAME = "PantallaBanksSettings";

interface IBank {
  bancoId: number;
  estadoId: number;
  estadoNombre: string;
  bancoNombre: string;
  codigoColones: string;
  codigoDolares: string;
}

const Banks: React.FC = () => {
  const [form] = Form.useForm<IBank>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [bancoId, setBancoId] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const formRef = React.useRef<FormInstance>(null);

  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();
  const [translatedData, setTranslatedData] = React.useState<{
    estados: { [key: string]: string };
  }>({
    estados: {},
  });
  const showErrorSavingToast = useErrorSavingDataToast();
  const showErrorUpdatingBankStateToast = useErrorUpdatingBankStateToast();
  const showSuccessUpdateToast = useSuccessUpdateToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const [getBanks, { data, isLoading }] = useGetBanksMutation();
  const [
    createBank,
    { isLoading: isLoadingCreate, isSuccess: isSuccessCreate, isError: isErrorCreate },
  ] = useCreateBankMutation();
  const [
    updateDate,
    { isLoading: isLoadingUpdate, isSuccess: isSuccessUpdate, isError: isErrorUpdate },
  ] = useUpdateBankMutation();
  const [
    setActive,
    { isLoading: isLoadingActive, isSuccess: isSuccessActive, isError: isErrorActive },
  ] = useSetBankStateActiveMutation();
  const [
    setInactive,
    { isLoading: isLoadingInactive, isSuccess: isSuccessInactive, isError: isErrorInactive },
  ] = useSetBankStateInactiveMutation();

  React.useEffect(() => {
    getBanks("");
  }, [
    getBanks,
    isSuccessCreate,
    isSuccessUpdate,
    isErrorCreate,
    isErrorUpdate,
    isSuccessActive,
    isSuccessInactive,
  ]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (isErrorCreate) showErrorSavingToast();

    if (isSuccessCreate) {
      showSuccessToast();
      form.resetFields();
      setBancoId(0);
    }
  }, [isSuccessCreate, form, isErrorCreate, showSuccessToast, showErrorSavingToast]);

  React.useEffect(() => {
    setDrawerOpen(false);
    if (isErrorUpdate) showErrorSavingToast();

    if (isSuccessUpdate) {
      showSuccessToast();
      form.resetFields();
      setBancoId(0);
    }
  }, [isSuccessUpdate, form, isErrorUpdate, showSuccessToast, showErrorSavingToast]);

  React.useEffect(() => {
    if (isErrorActive || isErrorInactive) {
      showErrorUpdatingBankStateToast();
    }

    if (isSuccessActive || isSuccessInactive) {
      showSuccessUpdateToast();
    }
  }, [isSuccessActive, isSuccessInactive, isErrorActive, isErrorInactive, showErrorUpdatingBankStateToast, showSuccessUpdateToast]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasData = data?.data && Array.isArray(data.data) && data.data.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasData) {
        setTranslatedData({
          estados: {},
        });
        return;
      }

      const estadosTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      // Recopilar todos los valores únicos que necesitan traducción
      data.data.forEach((record: IBank) => {
        // Traducir estado
        if (record.estadoNombre && !estadosTranslations.hasOwnProperty(record.estadoNombre)) {
          estadosTranslations[record.estadoNombre] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.estadoNombre, locale).then((translated) => {
              estadosTranslations[record.estadoNombre] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);

      // Actualizar estados con las traducciones
      setTranslatedData({
        estados: estadosTranslations as { [key: string]: string },
      });
    };

    translateData();
  }, [data?.data, locale]);

  const onClickEditarBanco = React.useCallback(
    (record: IBank) => {
      setBancoId(record.bancoId);
      form.setFieldsValue({
        ...record,
      });
      setDrawerOpen(true);
    },
    [form],
  );

  const cambiarEstadoBanco = React.useCallback(
    (value: number, id: number) => {
      if (value === 11) {
        setInactive(id);
      } else {
        setActive(id);
      }
    },
    [setActive, setInactive],
  );

  const submit = (values: any) => {
    const v = {
      ...values,
    };
    if (bancoId !== 0) {
      updateDate({
        ...v,
        id: bancoId,
      });
    } else {
      createBank(v);
    }
  };

  const columns = React.useMemo<ColumnsType<IBank>>(
    () => [
      {
        title: settingsPage?.bank_setting_table?.[0]?.label || "Banco",
        dataIndex: "bancoNombre",
      },
      {
        title: settingsPage?.bank_setting_table?.[1]?.label || "Código colones",
        dataIndex: "codigoColones",
      },
      {
        title: settingsPage?.bank_setting_table?.[2]?.label || "Código dólares",
        dataIndex: "codigoDolares",
      },
      {
        title: settingsPage?.bank_setting_table?.[3]?.label || "Estado",
        dataIndex: "estadoNombre",
        render: (text: string) => translatedData.estados[text] || text,
      },
      {
        title: "",
        dataIndex: "actions",
        fixed: "right",
        width: 100,
        render: (_, record) => (
          <Dropdown
            overlayClassName="table-dropdown"
            className="cursor-pointer"
            key={record.bancoId}
            trigger={["click"]}
            align={{ offset: [-40, 4] }}
            menu={{
              items: [
                {
                  key: "1",
                  label: settingsPage?.edit_button || "Editar",
                  icon: <Icon name="" color="#A7A7A7" />,
                  onClick: () => onClickEditarBanco(record),
                },
                {
                  key: "2",
                  label: record.estadoId === 11 ? (settingsPage?.inactive_button || "Inactivar") : (settingsPage?.active_button || "Activar"),
                  icon: <Icon name={record.estadoId === 11 ? "" : ""} color="#A7A7A7" />,
                  onClick: () => cambiarEstadoBanco(record.estadoId, record.bancoId),
                },
              ],
            }}
          >
            <Icon name="" isTouchable />
          </Dropdown>
        ),
      },
    ],
    [onClickEditarBanco, cambiarEstadoBanco, settingsPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <Content style={{ marginTop: "16px" }}>
        {isLoadingContent ? (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
              <Skeleton.Input active size="large" style={{ width: 200 }} />
              <Skeleton.Input active size="default" style={{ width: 150 }} />
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </>
        ) : (
          <>
            <Flex style={{ width: "100%", justifyContent: "space-between" }}>
              <LargeHeadingStyled>{settingsPage?.bank_nb || "Bancos"}</LargeHeadingStyled>
              <Flex style={{ gap: "15px" }}>
                <Button
                  title=""
                  type="primary"
                  onClick={() => {
                    setDrawerOpen(true);
                    form.resetFields();
                  }}
                >
                  {settingsPage?.bank_detail?.new_bank_button || "Nuevo banco"}
                </Button>
              </Flex>
            </Flex>
            <Card bodyStyle={{ padding: 0, width: "100%" }}>
              <Table<IBank>
                loading={
                  isLoading ||
                  isLoadingCreate ||
                  isLoadingUpdate ||
                  isLoadingActive ||
                  isLoadingInactive
                }
                columns={columns}
                dataSource={data?.data}
                rowKey={(v) => v.bancoId}
                pagination={{
                  defaultPageSize: 5,
                  showSizeChanger: true,
                  style: { marginRight: "30px" },
                }}
                scroll={{ x: 800 }}
              />
              <Drawer
                placement="right"
                title={bancoId !== 0 ? (settingsPage?.bank_detail?.title || "Edición de banco") : (settingsPage?.bank_detail?.new_bank_button || "Nuevo banco")}
                open={drawerOpen}
                onClose={() => {
                  setBancoId(0);
                  setDrawerOpen(false);
                }}
              >
                {" "}
                <Form
                  form={form}
                  layout="vertical"
                  ref={formRef}
                  onFinish={submit}
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                  initialValues={{
                    id: 1,
                  }}
                >
                  <Form.Item
                    name="bancoNombre"
                    label={settingsPage?.bank_detail?.name || "Nombre"}
                    hasFeedback
                    required
                    rules={[
                      {
                        required: true,
                        message: "Este campo es requerido.",
                      },
                    ]}
                  >
                    <Input type="text" />
                  </Form.Item>
                  <Form.Item
                    name="codigoColones"
                    label={settingsPage?.bank_detail?.colones_code || "Código colones"}
                    hasFeedback
                    required
                    rules={[
                      {
                        required: true,
                        message: "Este campo es requerido.",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="codigoDolares"
                    label={settingsPage?.bank_detail?.dollar_code || "Código dólares"}
                    hasFeedback
                    required
                    rules={[
                      {
                        required: true,
                        message: "Este campo es requerido.",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    style={{ alignSelf: "flex-end" }}
                    loading={isLoadingCreate || isLoadingActive || isLoadingInactive || isLoadingUpdate}
                  >
                    {settingsPage?.bank_detail?.save_button || "Guardar"}
                  </Button>
                </Form>
              </Drawer>
            </Card>
          </>
        )}
      </Content>
    </Flex>
  );
};

export default Banks;
